import styled, { css } from "styled-components";

// styles
import { regular as regularFontFamily } from "core/fontStyles";
import { easingSeconds } from "core/animation";
import { focusOutline } from "core/links";
import colors, { extendedColors } from "core/colors";

import Processing from "core/ReusableIcons/Processing";

// helpers
import {
    convertPxToRem,
    handleComponentSize
} from "frontend/components/helpers";
import { handleStyleType, handleBoxShadowOutlineBorder } from "./helpers";

export const SIZE_TYPES = {
    XS: css`
        height: ${convertPxToRem({ px: 24 })};
        font-size: ${convertPxToRem({ px: 12 })};
        line-height: ${convertPxToRem({ px: 12 })};
        padding-left: ${convertPxToRem({ px: 6 })};
        padding-right: ${convertPxToRem({ px: 6 })};

        svg {
            top: 0;
        }
    `,

    SM: css`
        height: ${convertPxToRem({ px: 28 })};
        font-size: ${convertPxToRem({ px: 12 })};
        line-height: ${convertPxToRem({ px: 12 })};
        padding-left: ${convertPxToRem({ px: 8 })};
        padding-right: ${convertPxToRem({ px: 8 })};

        svg {
            top: 0;
        }
    `,

    MD: css`
        height: ${convertPxToRem({ px: 40 })};
        font-size: ${convertPxToRem({ px: 13 })};
        line-height: ${convertPxToRem({ px: 13 })};
        padding-left: ${convertPxToRem({ px: 14 })};
        padding-right: ${convertPxToRem({ px: 14 })};

        ${Processing} {
            top: ${convertPxToRem({ px: 1 })};
        }
    `,

    LG: css`
        height: ${convertPxToRem({ px: 48 })};
        font-size: ${convertPxToRem({ px: 15 })};
        line-height: ${convertPxToRem({ px: 15 })};
        padding-left: ${convertPxToRem({ px: 16 })};
        padding-right: ${convertPxToRem({ px: 16 })};

        ${Processing} {
            top: ${convertPxToRem({ px: 1 })};
        }
    `
};

export const STYLE_MODES = {
    SOLID: {
        DARKMODE: css`
            ${({ isToggled, size }) => css`
                background: ${colors.mist};
                color: ${colors.ink};

                svg {
                    path,
                    use,
                    g,
                    circle {
                        fill: ${colors.midnight};
                    }
                }

                ${isToggled &&
                    css`
                        background: transparent;
                        color: ${colors.mist};

                        box-shadow: ${handleBoxShadowOutlineBorder({
                            size,
                            color: colors.mist
                        })};

                        svg {
                            path,
                            use,
                            g,
                            circle {
                                fill: ${colors.mist};
                            }
                        }
                    `}

                &:hover {
                    background: ${colors.pebble};
                    box-shadow: none;
                    color: ${colors.mist};

                    svg {
                        path,
                        use,
                        g,
                        circle {
                            fill: ${colors.mist};
                        }
                    }
                }
            `}
        `,
        LIGHTMODE: css`
            ${({ isToggled, size }) => css`
                background: ${colors.midnight};
                color: ${colors.mist};

                svg {
                    path,
                    use,
                    g,
                    circle {
                        fill: ${colors.mist};
                    }
                }

                ${isToggled &&
                    css`
                        background: transparent;
                        color: ${colors.midnight};

                        box-shadow: ${handleBoxShadowOutlineBorder({
                            size,
                            color: colors.midnight
                        })};

                        svg {
                            path,
                            use,
                            g,
                            circle {
                                fill: ${colors.midnight};
                            }
                        }
                    `}

                &:hover {
                    background: ${colors.porpoise};
                    box-shadow: none;

                    svg {
                        path,
                        use,
                        g,
                        circle {
                            fill: ${colors.mist};
                        }
                    }
                }
            `}
        `
    },
    OUTLINE: {
        DARKMODE: css`
            ${({ size, isToggled }) => css`
                background: transparent;
                color: ${colors.mist};

                box-shadow: ${handleBoxShadowOutlineBorder({
                    size,
                    color: colors.pebble
                })};

                svg {
                    path,
                    use,
                    g,
                    circle {
                        fill: ${colors.mist};
                    }
                }

                ${isToggled &&
                    css`
                        background: ${colors.mist};
                        color: ${colors.midnight};
                        box-shadow: none;

                        svg {
                            path,
                            use,
                            g,
                            circle {
                                fill: ${colors.midnight};
                            }
                        }
                    `}

                &:hover {
                    background: ${colors.pebble};
                    color: ${colors.mist};

                    box-shadow: ${handleBoxShadowOutlineBorder({
                        size,
                        color: "transparent"
                    })};
                }

                &:focus {
                    box-shadow: ${handleBoxShadowOutlineBorder({
                        size,
                        color: colors.pebble,
                        hasFocusShadow: true
                    })};
                }
            `}
        `,
        LIGHTMODE: css`
            ${({ size, isToggled }) => css`
                background: transparent;
                color: ${colors.midnight};

                box-shadow: ${handleBoxShadowOutlineBorder({
                    size,
                    color: colors.concrete
                })};

                svg {
                    path,
                    use,
                    g,
                    circle {
                        fill: ${colors.midnight};
                    }
                }

                ${isToggled &&
                    css`
                        background: ${colors.midnight};
                        color: ${colors.mist};
                        box-shadow: none;

                        svg {
                            path,
                            use,
                            g,
                            circle {
                                fill: ${colors.mist};
                            }
                        }
                    `}

                &:hover {
                    background: ${colors.darkModeLink};
                    color: ${colors.midnight};
                    box-shadow: ${handleBoxShadowOutlineBorder({
                        size,
                        color: colors.coin
                    })};
                }

                &:focus {
                    box-shadow: ${handleBoxShadowOutlineBorder({
                        size,
                        color: colors.concrete,
                        hasFocusShadow: true
                    })};
                }
            `}
        `
    },
    CTA: {
        DARKMODE: css`
            ${({ size, isToggled }) => css`
                background: ${colors.maya};
                color: ${colors.midnight};

                svg {
                    path,
                    use,
                    g,
                    circle {
                        fill: ${colors.midnight};
                    }
                }

                ${isToggled &&
                    css`
                        background: transparent;
                        box-shadow: ${handleBoxShadowOutlineBorder({
                            size,
                            color: colors.maya
                        })};
                        color: ${colors.maya};

                        svg {
                            path,
                            use,
                            g,
                            circle {
                                fill: ${colors.maya};
                            }
                        }
                    `}

                &:hover {
                    background: ${colors.mutedSoftBlue};
                    color: ${colors.mist};

                    ${isToggled &&
                        css`
                            box-shadow: none;
                        `}

                    svg {
                        path,
                        use,
                        g,
                        circle {
                            fill: ${colors.mist};
                        }
                    }
                }
            `}
        `,
        LIGHTMODE: css`
            ${({ size, isToggled }) => css`
                background: ${colors.ultramarine};
                color: ${colors.mist};

                svg {
                    path,
                    use,
                    g,
                    circle {
                        fill: ${colors.mist};
                    }
                }

                ${isToggled &&
                    css`
                        background: transparent;
                        box-shadow: ${handleBoxShadowOutlineBorder({
                            size,
                            color: colors.ultramarine
                        })};
                        color: ${colors.ultramarine};

                        svg {
                            path,
                            use,
                            g,
                            circle {
                                fill: ${colors.ultramarine};
                            }
                        }
                    `}

                &:hover {
                    background: ${colors.mutedRoyalBlue};
                    color: ${colors.mist};

                    ${isToggled &&
                        css`
                            box-shadow: none;
                        `}

                    svg {
                        path,
                        use,
                        g,
                        circle {
                            fill: ${colors.mist};
                        }
                    }
                }
            `}
        `
    },
    TEXT: {
        DARKMODE: css`
            background: transparent;
            color: ${colors.mist};

            svg {
                path,
                use,
                g,
                circle {
                    fill: ${colors.mist};
                }
            }

            &:hover {
                background: ${colors.pebble};
                color: ${colors.mist};

                svg {
                    path,
                    use,
                    g,
                    circle {
                        fill: ${colors.mist};
                    }
                }
            }
        `,
        LIGHTMODE: css`
            background: transparent;
            color: ${colors.storm};

            svg {
                path,
                use,
                g,
                circle {
                    fill: ${colors.storm};
                }
            }

            &:hover {
                background: ${extendedColors.textButtonHover};
                color: ${colors.midnight};

                svg {
                    path,
                    use,
                    g,
                    circle {
                        fill: ${colors.midnight};
                    }
                }
            }
        `
    },
    NONE: {
        DARKMODE: css`
            background: transparent;
            color: ${colors.mist};
            height: auto;
            padding: 0;

            svg {
                path,
                use,
                g,
                circle {
                    fill: ${colors.mist};
                }
            }

            &:hover {
                background: transparent;
                color: ${colors.mist};

                svg {
                    path,
                    use,
                    g,
                    circle {
                        fill: ${colors.mist};
                    }
                }
            }
        `,
        LIGHTMODE: css`
            background: transparent;
            color: ${colors.storm};
            height: auto;
            padding: 0;

            svg {
                path,
                use,
                g,
                circle {
                    fill: ${colors.storm};
                }
            }

            &:hover {
                background: transparent;
                color: ${colors.midnight};

                svg {
                    path,
                    use,
                    g,
                    circle {
                        fill: ${colors.midnight};
                    }
                }
            }
        `
    }
};

export const STYLE_TYPES = {
    SOLID: css`
        ${({ darkMode }) =>
            darkMode ? STYLE_MODES.SOLID.DARKMODE : STYLE_MODES.SOLID.LIGHTMODE}
    `,

    OUTLINE: css`
        ${({ darkMode }) =>
            darkMode
                ? STYLE_MODES.OUTLINE.DARKMODE
                : STYLE_MODES.OUTLINE.LIGHTMODE}
    `,

    CTA: css`
        ${({ darkMode }) =>
            darkMode ? STYLE_MODES.CTA.DARKMODE : STYLE_MODES.CTA.LIGHTMODE}
    `,

    TEXT: css`
        ${({ darkMode }) =>
            darkMode ? STYLE_MODES.TEXT.DARKMODE : STYLE_MODES.TEXT.LIGHTMODE}
    `,

    NONE: css`
        ${({ darkMode }) =>
            darkMode ? STYLE_MODES.NONE.DARKMODE : STYLE_MODES.NONE.LIGHTMODE}
    `
};

const ACTIVE_STYLE = ({ darkMode }) => css`
    background: ${darkMode
        ? extendedColors.darkMidnight
        : extendedColors.cloud};
    color: ${darkMode ? colors.mist : colors.midnight};
    box-shadow: none;
    border: none;
    outline: none;

    svg {
        path,
        use,
        g,
        circle {
            transition: all ${easingSeconds};
            fill: ${darkMode ? colors.mist : colors.midnight};
        }
    }

    &:hover {
        background: ${darkMode
            ? extendedColors.cloud
            : extendedColors.darkMidnight};
        color: ${darkMode ? colors.midnight : colors.mist};
        box-shadow: none;
        border: none;
        outline: none;

        svg {
            path,
            use,
            g,
            circle {
                fill: ${darkMode ? colors.midnight : colors.mist};
            }
        }
    }
`;

const PROCESSING_STYLE = ({ darkMode }) => css`
    background: ${darkMode ? extendedColors.abyss : extendedColors.cloud};
    color: ${darkMode ? colors.mist : colors.midnight};

    ${Processing} path {
        fill: ${darkMode ? colors.mist : colors.midnight};
    }
`;

export const ButtonDefaults = css`
    ${regularFontFamily}
    border: none;
    background: none;
    text-decoration: none;
    padding: 0;
    margin: 0;
    transition: all ${easingSeconds};
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    white-space: nowrap;

    width: ${({ width }) => (!!width ? width : "auto")};

    justify-content: ${({ justifyContent }) => justifyContent};

    border-radius: ${({ isPill }) => convertPxToRem({ px: isPill ? 48 : 4 })};

    svg {
        position: relative;
        top: ${convertPxToRem({ px: 1 })};
        width: ${convertPxToRem({ px: 12 })};
        height: ${convertPxToRem({ px: 12 })};
        ${({ iconPosition, isProcessing }) =>
            iconPosition === "left" && !isProcessing
                ? css`
                      margin-right: ${convertPxToRem({ px: 8 })};
                  `
                : css`
                      margin-left: ${convertPxToRem({ px: 8 })};
                  `};

        path,
        use,
        g,
        circle {
            transition: all ${easingSeconds};
        }
    }

    &:focus {
        transition: all 0.1s;
        ${focusOutline};
    }

    ${({ size }) => handleComponentSize({ size, styles: SIZE_TYPES })}
    ${({ styleType }) => handleStyleType({ styleType, STYLE_TYPES })}
`;

export const AllButtonStyles = css`
     ${ButtonDefaults}

    /* active, isDisabled, isProcessing- stays last so they override properties such as background color, color, etc */
    ${({ isActive }) => isActive && ACTIVE_STYLE}

    &:active {
        ${ACTIVE_STYLE}
    }

    ${({ isDisabled }) =>
        isDisabled &&
        css`
            pointer-events: none;
            background: ${colors.coin};
            color: ${colors.storm};
            border: none;
            box-shadow: none;
            outline: none;
        `}

    ${({ isProcessing, darkMode }) =>
        isProcessing && PROCESSING_STYLE({ darkMode })}
`;

// the actual Button-type element:
export const StyledButton = styled.span`
    &,
    &:visited {
        ${AllButtonStyles}
    }
`;
